import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Edit, Eye, EyeOff, Globe, Loader2, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoriesTab = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState({ id: '', name: '' });
  const [newSub, setNewSub] = useState({ catId: '', id: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [editingSeo, setEditingSeo] = useState(null);
  const [seoForm, setSeoForm] = useState({ seo_title: '', seo_description: '', seo_keywords: '' });

  const fetchCats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCats(); }, [fetchCats]);

  const handleAddCategory = async () => {
    if (!newCat.id.trim() || !newCat.name.trim()) { toast.error('ID and Name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/categories`, newCat, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category added!');
      setNewCat({ id: '', name: '' });
      fetchCats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (catId, field, value) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}`, { [field]: value }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCats();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${catId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchCats();
    } catch { toast.error('Failed'); }
  };

  const handleAddSub = async () => {
    if (!newSub.catId || !newSub.id.trim() || !newSub.name.trim()) { toast.error('All fields required'); return; }
    try {
      await axios.post(`${API}/admin/categories/${newSub.catId}/subcategories`, { id: newSub.id, name: newSub.name }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Subcategory added!');
      setNewSub({ catId: '', id: '', name: '' });
      fetchCats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSub = async (catId, subId) => {
    try {
      await axios.delete(`${API}/admin/categories/${catId}/subcategories/${subId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchCats();
    } catch { toast.error('Failed'); }
  };

  const handleEditSeo = (cat) => {
    if (editingSeo === cat.id) { setEditingSeo(null); return; }
    setEditingSeo(cat.id);
    setSeoForm({
      seo_title: cat.seo_title || '',
      seo_description: cat.seo_description || '',
      seo_keywords: cat.seo_keywords || ''
    });
  };

  const handleSaveSeo = async (catId) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}`, seoForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SEO saved!');
      setEditingSeo(null);
      fetchCats();
    } catch { toast.error('Failed to save SEO'); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-categories">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Category Manager</h2>
          <p className="text-sm text-gray-500">Control sidebar &amp; navigation visibility</p>
        </div>
      </div>

      {/* Add New Category */}
      <div className="bg-white border border-gray-200 rounded p-5">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Add New Category</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">ID (lowercase, no spaces)</label>
            <input value={newCat.id} onChange={e => setNewCat({...newCat, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
              placeholder="e.g. anime" className="w-full px-3 py-2 border border-gray-200 rounded text-sm" data-testid="new-cat-id" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Display Name</label>
            <input value={newCat.name} onChange={e => setNewCat({...newCat, name: e.target.value})}
              placeholder="e.g. Anime & Manga" className="w-full px-3 py-2 border border-gray-200 rounded text-sm" data-testid="new-cat-name" />
          </div>
          <button onClick={handleAddCategory} disabled={saving}
            className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40"
            data-testid="add-cat-btn">
            Add
          </button>
        </div>
      </div>

      {/* Add Subcategory */}
      <div className="bg-white border border-gray-200 rounded p-5">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Add Subcategory</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Parent Category</label>
            <select value={newSub.catId} onChange={e => setNewSub({...newSub, catId: e.target.value})}
              className="px-3 py-2 border border-gray-200 rounded text-sm" data-testid="new-sub-cat">
              <option value="">Select...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Sub ID</label>
            <input value={newSub.id} onChange={e => setNewSub({...newSub, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
              placeholder="e.g. anime_shonen" className="px-3 py-2 border border-gray-200 rounded text-sm" data-testid="new-sub-id" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Name</label>
            <input value={newSub.name} onChange={e => setNewSub({...newSub, name: e.target.value})}
              placeholder="e.g. Shonen" className="px-3 py-2 border border-gray-200 rounded text-sm" data-testid="new-sub-name" />
          </div>
          <button onClick={handleAddSub}
            className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700"
            data-testid="add-sub-btn">
            Add
          </button>
        </div>
      </div>

      {/* Category List */}
      <div className="space-y-3">
        {categories.map(cat => (
          <div key={cat.id} className="bg-white border border-gray-200 rounded overflow-hidden" data-testid={`cat-row-${cat.id}`}>
            <div className="flex items-center gap-4 px-5 py-3">
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-sm">{cat.name}</p>
                <p className="text-xs text-gray-400 font-mono">{cat.id}</p>
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={cat.visible_sidebar !== false}
                  onChange={e => handleToggle(cat.id, 'visible_sidebar', e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-gray-900" />
                <span className="text-xs text-gray-600">Sidebar</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={cat.visible_nav === true}
                  onChange={e => handleToggle(cat.id, 'visible_nav', e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-gray-900" />
                <span className="text-xs text-gray-600">Top Nav</span>
              </label>
              <button onClick={() => handleEditSeo(cat)}
                className={`p-1 transition-colors ${editingSeo === cat.id ? 'text-orange-500' : 'text-gray-400 hover:text-blue-500'}`}
                data-testid={`seo-btn-${cat.id}`} title="Edit SEO">
                <Globe className="w-4 h-4" />
              </button>
              <button onClick={() => handleDeleteCat(cat.id)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-cat-${cat.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {/* SEO Fields */}
            {editingSeo === cat.id && (
              <div className="border-t border-gray-200 px-5 py-3 bg-blue-50/50 space-y-2" data-testid={`seo-panel-${cat.id}`}>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">SEO Settings</p>
                <div>
                  <label className="text-xs text-gray-500 mb-0.5 block">Meta Title</label>
                  <input value={seoForm.seo_title} onChange={e => setSeoForm({...seoForm, seo_title: e.target.value})}
                    placeholder={`${cat.name} Posters | PosterFire`}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm text-gray-900" data-testid={`seo-title-${cat.id}`} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-0.5 block">Meta Description</label>
                  <textarea value={seoForm.seo_description} onChange={e => setSeoForm({...seoForm, seo_description: e.target.value})}
                    placeholder={`Shop premium ${cat.name.toLowerCase()} posters and wall art at PosterFire.com.`}
                    rows={2} className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm text-gray-900 resize-none" data-testid={`seo-desc-${cat.id}`} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-0.5 block">SEO Keywords (comma separated)</label>
                  <input value={seoForm.seo_keywords} onChange={e => setSeoForm({...seoForm, seo_keywords: e.target.value})}
                    placeholder={`${cat.name.toLowerCase()} posters, wall art, prints`}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm text-gray-900" data-testid={`seo-keywords-${cat.id}`} />
                </div>
                <div className="flex justify-end">
                  <button onClick={() => handleSaveSeo(cat.id)}
                    className="px-4 py-1.5 bg-gray-900 text-white text-xs font-bold rounded hover:bg-gray-700"
                    data-testid={`seo-save-${cat.id}`}>
                    Save SEO
                  </button>
                </div>
              </div>
            )}
            {cat.subcategories?.length > 0 && (
              <div className="border-t border-gray-100 px-5 py-2 bg-gray-50">
                <div className="flex flex-wrap gap-2">
                  {cat.subcategories.map(sub => (
                    <span key={sub.id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700">
                      {sub.name}
                      <button onClick={() => handleDeleteSub(cat.id, sub.id)} className="text-gray-400 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


export default CategoriesTab;
